<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import {
  MagnifyingGlassIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  UserCircleIcon,
  AdjustmentsHorizontalIcon,
  EnvelopeIcon,
  CreditCardIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/vue/24/outline'
import { useQuoteStore } from '@/stores/useQuoteStore'
import { useOrderStore } from '@/stores/useOrderStore'
import { useRegulationStore } from '@/stores/useRegulationStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'
import { useToast } from 'vue-toast-notification'
import { REGULATION_STATUS } from '@/constants/regulationStatus'
const router = useRouter()
const route = useRoute()
const quoteStore = useQuoteStore()
const orderStore = useOrderStore()
const regulationStore = useRegulationStore()
const boostCoreStore = useBoostCorePermissionStore()
const usersStore = useBoostCoreUsersStore()
const $toast = useToast()

const canAdvWrite = computed(() => boostCoreStore.can('ADV_WRITE'))
const canCreateRegulation = computed(() => boostCoreStore.can('SALES_READ') || boostCoreStore.can('ADV_WRITE'))

const searchQuery = ref('')
const isSearching = ref(false)
const hasSearched = ref(false)
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
const selectedQuote = ref<any | null>(null)
const orderByQuote = ref<any | null>(null)
const quoteCommissions = ref<any | null>(null)

const amount = ref<number>(0)
const reason = ref('')
const selectedFamily = ref<string | null>(null)
const selectedSalesEngineerUuid = ref<string | null>(null)
const commissionForRegulation = ref<any | null>(null)
const contractCoef = ref<number | ''>('')
const durationCoef = ref<number | ''>('')
const paymentCoef = ref<number | ''>('')
const iavCoef = ref<number | ''>('')
const isSaving = ref(false)
const showModalCreate = ref(false)
const showModalCreateAndValidate = ref(false)

onMounted(async () => {
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  await Promise.all([usersStore.fetchUsers(), regulationStore.fetchFamilyOptions()])
  const quoteRef = route.query.quoteReference as string | undefined
  if (quoteRef) {
    searchQuery.value = decodeURIComponent(quoteRef)
    await onSearch()
  }
})

watch(selectedQuote, (q) => {
  if (q?.salesEngineerUuid) {
    selectedSalesEngineerUuid.value = q.salesEngineerUuid
  }
})

const userOptions = computed(() => {
  const active = usersStore.users.filter((u) => u.status === 'ACTIVE')
  return active.map((u) => ({
    id: u.uuid,
    value: u.uuid,
    label: usersStore.getUserLabel(u),
  }))
})

const familyOptions = computed(() => regulationStore.familyOptions)

const quoteDevisInfos = computed(() => {
  const q = selectedQuote.value
  if (!q) return null
  const cc = q.computedCommercial as
    | { margeCompteur?: number; margeTotalContrat?: number; totalCaContratHt?: number }
    | undefined
  const useApi =
    cc &&
    typeof cc.margeCompteur === 'number' &&
    typeof cc.margeTotalContrat === 'number' &&
    typeof cc.totalCaContratHt === 'number'
  const marge = Number(q.marge) || 0
  const ca = Number(q.totalCa?.toFixed(2) || 0)
  let margeCompteur: number
  let margeTotal: number
  let totalCAVal: number
  if (useApi && cc) {
    margeCompteur = cc.margeCompteur!
    margeTotal = cc.margeTotalContrat!
    totalCAVal = cc.totalCaContratHt!
  } else {
    margeCompteur = marge
    margeTotal = marge
    totalCAVal = ca
  }
  const orderMargeCompteur = orderByQuote.value?.margeCompteur ?? null
  const orderForceMarge = orderByQuote.value?.forceMarge ?? null
  const effectiveMargeCompteur = orderForceMarge != null ? orderForceMarge : (orderMargeCompteur ?? margeCompteur)
  const isMargeForced = orderForceMarge != null
  const margeDiff = orderForceMarge != null ? orderForceMarge - margeCompteur : null
  const margeOrderDevisDiff =
    orderMargeCompteur != null && Math.abs(margeCompteur - orderMargeCompteur) > 0.001
      ? margeCompteur - orderMargeCompteur
      : null
  return {
    signedAt: q.signedAt ? new Date(q.signedAt).toLocaleDateString('fr-FR') : '-',
    signedPdfUrl: (q.metadata as { signedPdfUrl?: string })?.signedPdfUrl ?? null,
    duration: q.subscriptionDuration?.label || q.subscriptionDurationCode || '-',
    margeTotal,
    margeCompteur,
    orderMargeCompteur,
    margeOrderDevisDiff,
    effectiveMargeCompteur,
    isMargeForced,
    orderForceMarge,
    margeDiff,
    totalCA: totalCAVal,
    modePaiement: q.effectivePaymentMode?.type || q.effectivePaymentMode?.code || q.paymentMode?.type || '-',
    orderType: q.orderType || '-',
  }
})

const commercialDisplay = computed(() => {
  const se = selectedQuote.value?.salesEngineer
  if (!se) return 'Information non saisie'
  const name = [se.firstName, se.lastName].filter(Boolean).join(' ')
  return name || se.email || 'Information non saisie'
})

const canSave = computed(() => {
  const amt = amount.value
  const validAmount = typeof amt === 'number' && !Number.isNaN(amt) && amt !== 0
  return selectedQuote.value && selectedSalesEngineerUuid.value && selectedFamily.value && validAmount
})

function getOrderFormId(order: any): string | null {
  const t = order?.orderTemplate?.template
  return t && typeof t === 'object' && t !== null && 'formId' in t ? String((t as Record<string, unknown>).formId) : null
}

function onSearchQueryInput() {
  if (selectedQuote.value) return
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  if (!searchQuery.value.trim()) return
  searchDebounceTimer = setTimeout(() => onSearch(), 600)
}

function onSearchQueryPaste(event: ClipboardEvent) {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  const pasted = event.clipboardData?.getData('text')?.trim()
  if (!pasted) return
  setTimeout(() => onSearch(), 0)
}

async function onSearch() {
  if (!searchQuery.value?.trim()) return
  isSearching.value = true
  hasSearched.value = false
  selectedQuote.value = null
  orderByQuote.value = null
  quoteCommissions.value = null
  commissionForRegulation.value = null
  contractCoef.value = ''
  durationCoef.value = ''
  paymentCoef.value = ''
  iavCoef.value = ''
  try {
    await quoteStore.syncQuote(searchQuery.value.trim())
    const response = await quoteStore.fetchQuote(searchQuery.value.trim())
    if (response) {
      const data = response.data || response
      const quote = Array.isArray(data) ? data[0] : data
      if (quote) {
        selectedQuote.value = quote
        searchQuery.value = quote.quoteReference
        const [order, commData] = await Promise.all([
          orderStore.fetchOrderByQuote(quote.uuid),
          regulationStore.fetchQuoteCommissions(quote.uuid),
        ])
        orderByQuote.value = order
        quoteCommissions.value = commData
      }
    }
  } catch (error) {
    console.error('Erreur lors de la recherche du devis:', error)
    $toast.error('Erreur lors de la recherche du devis')
  } finally {
    isSearching.value = false
    hasSearched.value = true
  }
}

function prefillCommissionForRegulation(commission: any) {
  commissionForRegulation.value = commission
  amount.value = -(commission.margeCompteur ?? 0)
  selectedSalesEngineerUuid.value = commission.salesEngineerUuid ?? null
  const orderTemplateCode = quoteCommissions.value?.order?.orderTemplateCode
  const fam = orderTemplateCode
    ? regulationStore.familyOptions.find((f: any) => (f.value ?? f.id) === orderTemplateCode)
    : null
  selectedFamily.value = fam ? (fam.value ?? fam.id) : null
  contractCoef.value = commission.contractCoef ?? ''
  durationCoef.value = commission.durationCoef ?? ''
  paymentCoef.value = commission.paymentCoef ?? ''
  iavCoef.value = commission.iavCoef ?? ''
}

async function onSave(andValidate = false) {
  showModalCreate.value = false
  showModalCreateAndValidate.value = false
  if (!canSave.value || !selectedQuote.value || !selectedSalesEngineerUuid.value) return
  const amt = amount.value
  if (amt == null || amt === 0 || Number.isNaN(amt)) return
  isSaving.value = true
  try {
    const payload: Parameters<typeof regulationStore.createRegulation>[0] = {
      quoteUuid: selectedQuote.value.uuid,
      salesEngineerUuid: selectedSalesEngineerUuid.value,
      amount: amount.value,
      family: selectedFamily.value!,
      reason: reason.value || undefined,
      ...(contractCoef.value !== '' || durationCoef.value !== '' || paymentCoef.value !== '' || iavCoef.value !== ''
        ? {
            contractCoef: contractCoef.value !== '' ? Number(contractCoef.value) : undefined,
            durationCoef: durationCoef.value !== '' ? Number(durationCoef.value) : undefined,
            paymentCoef: paymentCoef.value !== '' ? Number(paymentCoef.value) : undefined,
            iavCoef: iavCoef.value !== '' ? Number(iavCoef.value) : undefined,
          }
        : commissionForRegulation.value
          ? {
              contractCoef: commissionForRegulation.value.contractCoef,
              durationCoef: commissionForRegulation.value.durationCoef,
              paymentCoef: commissionForRegulation.value.paymentCoef,
              iavCoef: commissionForRegulation.value.iavCoef,
            }
          : {}),
    }
    if (andValidate) {
      payload.validatedAt = new Date().toISOString()
      payload.advUuid = boostCoreStore.user?.uuid
    }
    const result = await regulationStore.createRegulation(payload)
    if (result) {
      $toast.success(andValidate ? 'Régularisation créée et validée avec succès' : 'Régularisation créée avec succès', { duration: 4000 })
      router.push('/dashboard/orders')
    } else {
      $toast.error('Erreur lors de la création de la régularisation')
    }
  } catch {
    $toast.error('Erreur lors de la création de la régularisation')
  } finally {
    isSaving.value = false
  }
}

function goBack() {
  router.push('/dashboard/orders')
}
</script>

<template>
  <div class="min-h-screen bg-base-200/40">
    <div class="px-4 py-6 max-w-[1600px] mx-auto w-full">
      <div class="flex items-start justify-between gap-4">
        <div>
          <div class="flex items-center gap-1">
            <h1 class="text-2xl font-semibold">Créer une régularisation</h1>
            <HelpButton />
          </div>
          <p class="text-sm text-base-content/70">Recherche un devis, puis remplis les informations de la régularisation.</p>
        </div>
        <ButtonDaisy text="Retour" variant="ghost" @click="goBack" />
      </div>

      <div class="mt-6 space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Card Devis -->
          <div class="card bg-base-100 shadow">
            <div class="card-body">
              <div class="flex items-center justify-between">
                <h2 class="card-title text-lg gap-2">
                  <BanknotesIcon class="w-5 h-5" />
                  1) Devis
                </h2>
                <span class="badge badge-ghost">Obligatoire</span>
              </div>
              <div class="flex gap-2 items-end">
                <TextInput
                  v-model="searchQuery"
                  label="Référence du devis"
                  placeholder="Ex: SO12345"
                  class="flex-1"
                  @keyup.enter="onSearch"
                  @input="onSearchQueryInput"
                  @paste="onSearchQueryPaste"
                />
                <ButtonDaisy text="Rechercher" variant="primary" :icon="MagnifyingGlassIcon" :loader="isSearching" @click="onSearch" />
              </div>
              <p v-if="hasSearched && searchQuery && !isSearching && !selectedQuote" class="mt-2 text-sm text-base-content/50 italic">
                Aucun devis trouvé pour "{{ searchQuery }}".
              </p>
              <div v-if="selectedQuote" class="mt-4 p-4 bg-success/10 rounded-lg space-y-3">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-bold">{{ selectedQuote.quoteReference }}</p>
                  <a
                    v-if="(selectedQuote as any).odooUrl || selectedQuote.metadata?.odooUrl"
                    :href="(selectedQuote as any).odooUrl || selectedQuote.metadata.odooUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-primary underline opacity-70 hover:opacity-100 flex items-center gap-0.5"
                    title="Voir dans Odoo"
                  >
                    <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                    Odoo
                  </a>
                </div>
                <p class="text-xs opacity-70 truncate">{{ selectedQuote.company?.name }}</p>
                <div v-if="quoteDevisInfos" class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
                  <div class="flex items-center gap-2 min-w-0">
                    <BanknotesIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-60">Total CA</p>
                      <p class="font-medium text-sm">{{ quoteDevisInfos.totalCA.toFixed(2) }} €</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 min-w-0">
                    <ChartBarIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-60 flex items-center gap-1">
                        Marge compteur
                        <span v-if="quoteDevisInfos.isMargeForced" class="text-warning font-semibold">(forcée)</span>
                      </p>
                      <p class="font-medium text-sm">{{ quoteDevisInfos.margeCompteur.toFixed(2) }} €</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 min-w-0">
                    <CalendarDaysIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-60">Signé le</p>
                      <p class="font-medium text-sm">
                        {{ quoteDevisInfos.signedAt }}
                        <a
                          v-if="quoteDevisInfos.signedPdfUrl"
                          :href="quoteDevisInfos.signedPdfUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="link link-primary ml-1 inline-flex items-center gap-0.5"
                          title="Voir le devis signé"
                        >
                          <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                        </a>
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 min-w-0">
                    <ClockIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-60">Durée</p>
                      <p class="font-medium text-sm">{{ quoteDevisInfos.duration }}</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 min-w-0">
                    <CreditCardIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-60">Mode paiement</p>
                      <p class="font-medium text-sm">{{ quoteDevisInfos.modePaiement }}</p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 min-w-0">
                    <DocumentTextIcon class="w-4 h-4 shrink-0 opacity-60" />
                    <div class="min-w-0">
                      <p class="text-xs opacity-60">Type</p>
                      <p class="font-medium text-sm">{{ quoteDevisInfos.orderType }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Card Client -->
          <div class="card bg-base-100 shadow h-full">
            <div class="card-body">
              <h2 class="card-title text-lg">
                <BuildingOffice2Icon class="w-5 h-5" />
                Client
              </h2>
              <div v-if="selectedQuote?.company" class="p-4 bg-base-200/50 rounded-lg mt-2 space-y-4">
                <div class="flex gap-3 min-w-0">
                  <BuildingOffice2Icon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-70">Raison sociale</p>
                    <p class="font-semibold truncate">{{ selectedQuote.company?.name }}</p>
                  </div>
                </div>
                <div class="flex gap-3 flex-wrap">
                  <div class="flex items-start gap-2 min-w-0">
                    <DocumentTextIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div>
                      <p class="text-xs opacity-70">SIREN</p>
                      <p class="font-medium text-sm">{{ selectedQuote.company?.siren || '-' }}</p>
                    </div>
                  </div>
                  <div class="flex items-start gap-2 min-w-0">
                    <DocumentTextIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                    <div>
                      <p class="text-xs opacity-70">SIRET</p>
                      <p class="font-medium text-sm">{{ selectedQuote.company?.siret || '-' }}</p>
                    </div>
                  </div>
                </div>
                <div class="flex gap-3 min-w-0">
                  <UserCircleIcon class="w-4 h-4 shrink-0 opacity-60 mt-0.5" />
                  <div class="min-w-0">
                    <p class="text-xs opacity-70">Commercial</p>
                    <p class="text-sm font-medium">{{ commercialDisplay }}</p>
                    <p class="text-xs opacity-60 flex items-center gap-1 mt-0.5">
                      <EnvelopeIcon class="w-3.5 h-3.5 shrink-0" />
                      {{ selectedQuote.salesEngineer?.email || '-' }}
                    </p>
                  </div>
                </div>
              </div>
              <p v-else class="text-sm text-base-content/50 italic mt-2">Sélectionne un devis pour afficher le client.</p>
            </div>
          </div>
        </div>

        <!-- Commande liée -->
        <div v-if="orderByQuote" class="card bg-base-100 shadow">
          <div class="card-body">
            <h2 class="card-title text-lg gap-2">
              <DocumentTextIcon class="w-5 h-5" />
              Commande liée
            </h2>
            <div class="flex flex-wrap items-center gap-4 mt-3">
              <div class="flex flex-col gap-2">
                <div class="flex items-center gap-2">
                  <span class="badge badge-sm" :class="{
                    'badge-ghost': orderByQuote.status === 'DRAFT',
                    'badge-warning': orderByQuote.status === 'WAITING',
                    'badge-info': ['IN_PROGRESS', 'BLOCKED_BUSINESS', 'BLOCKED_OPERATOR'].includes(orderByQuote.status),
                    'badge-success': orderByQuote.status === 'VALIDATED',
                    'badge-error': orderByQuote.status === 'CANCELED',
                  }">{{ orderByQuote.status }}</span>
                  <span v-if="orderByQuote.margeCompteur != null" class="text-sm text-base-content/70">
                    Marge order : {{ orderByQuote.margeCompteur.toFixed(2) }} €
                  </span>
                  <span v-if="orderByQuote.forceMarge != null" class="text-sm text-base-content/70">
                    · Forcée : {{ orderByQuote.forceMarge.toFixed(2) }} €
                  </span>
                  <span v-if="orderByQuote.orderTemplateCode" class="text-sm text-base-content/60">
                    · {{ getOrderFormId(orderByQuote) ?? orderByQuote.orderTemplateCode }}
                  </span>
                </div>
                <div v-if="quoteDevisInfos?.orderMargeCompteur != null" class="text-sm">
                  <span class="opacity-70">Devis à jour : {{ quoteDevisInfos.margeCompteur.toFixed(2) }} €</span>
                  <span
                    v-if="quoteDevisInfos.margeOrderDevisDiff != null"
                    class="text-error font-semibold ml-2"
                  >
                    diff : {{ quoteDevisInfos.margeOrderDevisDiff > 0 ? '+' : '' }}{{ quoteDevisInfos.margeOrderDevisDiff.toFixed(2) }} €
                  </span>
                </div>
              </div>
              <a
                :href="`/dashboard/order/${orderByQuote.uuid}`"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-sm btn-ghost gap-1"
              >
                <ArrowTopRightOnSquareIcon class="w-4 h-4" />
                Voir la commande
              </a>
            </div>
          </div>
        </div>

        <!-- Commissions existantes -->
        <div v-if="quoteCommissions?.order?.commissions?.length || quoteCommissions?.regulations?.length" class="card bg-base-100 shadow">
          <div class="card-body">
            <h2 class="card-title text-lg gap-2">
              <ChartBarIcon class="w-5 h-5" />
              Commissions & Régularisations existantes
            </h2>
            <div v-if="quoteCommissions.order?.commissions?.length" class="mt-3">
              <h3 class="text-sm font-semibold mb-2">Commissions</h3>
              <div class="overflow-x-auto">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Commercial</th>
                      <th>Marge compteur</th>
                      <th>Coef pay.</th>
                      <th>Coef dur.</th>
                      <th>Coef contr.</th>
                      <th>Coef IAV</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in quoteCommissions.order.commissions" :key="c.uuid">
                      <td>{{ c.salesEngineer?.firstName }} {{ c.salesEngineer?.lastName }}</td>
                      <td>{{ c.margeCompteur?.toFixed(2) }} €</td>
                      <td>{{ c.paymentCoef != null ? c.paymentCoef.toFixed(2) : '-' }}</td>
                      <td>{{ c.durationCoef != null ? c.durationCoef.toFixed(2) : '-' }}</td>
                      <td>{{ c.contractCoef != null ? c.contractCoef.toFixed(2) : '-' }}</td>
                      <td>{{ c.iavCoef != null ? c.iavCoef.toFixed(2) : '-' }}</td>
                      <td>
                        <button class="btn btn-ghost btn-xs text-error" @click="prefillCommissionForRegulation(c)">
                          Réguler (annuler)
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-if="quoteCommissions.regulations?.length" class="mt-4">
              <h3 class="text-sm font-semibold mb-2">Régularisations existantes</h3>
              <div class="overflow-x-auto">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Commercial</th>
                      <th>Montant</th>
                      <th>Coef pay.</th>
                      <th>Coef dur.</th>
                      <th>Coef contr.</th>
                      <th>Coef IAV</th>
                      <th>Statut</th>
                      <th>Motif</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in quoteCommissions.regulations" :key="r.uuid">
                      <td>{{ r.salesEngineer?.firstName }} {{ r.salesEngineer?.lastName }}</td>
                      <td :class="r.amount < 0 ? 'text-error' : 'text-success'">{{ r.amount?.toFixed(2) }} €</td>
                      <td>{{ r.paymentCoef != null ? r.paymentCoef.toFixed(2) : '-' }}</td>
                      <td>{{ r.durationCoef != null ? r.durationCoef.toFixed(2) : '-' }}</td>
                      <td>{{ r.contractCoef != null ? r.contractCoef.toFixed(2) : '-' }}</td>
                      <td>{{ r.iavCoef != null ? r.iavCoef.toFixed(2) : '-' }}</td>
                      <td>
                        <span class="badge badge-sm" :class="{
                          'badge-ghost': r.status === REGULATION_STATUS.WAITING,
                          'badge-success': r.status === REGULATION_STATUS.VALIDATED,
                          'badge-error': r.status === REGULATION_STATUS.CANCELED,
                        }">{{ r.status }}</span>
                      </td>
                      <td class="max-w-[200px] truncate">{{ r.reason || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Formulaire régularisation -->
        <div v-if="selectedQuote" class="card bg-base-100 shadow">
          <div class="card-body">
            <h2 class="card-title text-lg gap-2">
              <AdjustmentsHorizontalIcon class="w-5 h-5" />
              2) Régulation
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <SelectInput
                v-model="selectedSalesEngineerUuid"
                label="Commercial *"
                :options="userOptions"
                placeholder="Sélectionner le commercial"
              />
              <SelectInput
                v-model="selectedFamily"
                label="Famille *"
                :options="familyOptions"
                placeholder="Sélectionner une famille"
              />
              <div class="form-control w-full">
                <label class="label">
                  <span class="label-text font-semibold">Marge Compteur * (négatif ou positif)</span>
                </label>
                <div class="input input-bordered input-sm flex items-center gap-2">
                  <input
                    v-model.number="amount"
                    type="number"
                    step="0.01"
                    class="w-full outline-none bg-transparent"
                    placeholder="0,00"
                  />
                  <span class="text-base-content/60 font-medium">€</span>
                </div>
              </div>
            </div>
            <div class="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-sm">Coef pay.</span>
                </label>
                <input
                  v-model.number="paymentCoef"
                  type="number"
                  step="0.01"
                  class="input input-bordered input-sm"
                  placeholder="Repris de la commission"
                />
              </div>
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-sm">Coef dur.</span>
                </label>
                <input
                  v-model.number="durationCoef"
                  type="number"
                  step="0.01"
                  class="input input-bordered input-sm"
                  placeholder="Repris de la commission"
                />
              </div>
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-sm">Coef contr.</span>
                </label>
                <input
                  v-model.number="contractCoef"
                  type="number"
                  step="0.01"
                  class="input input-bordered input-sm"
                  placeholder="Repris de la commission"
                />
              </div>
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-sm">Coef IAV</span>
                </label>
                <input
                  v-model.number="iavCoef"
                  type="number"
                  step="0.01"
                  class="input input-bordered input-sm"
                  placeholder="Repris de la commission"
                />
              </div>
            </div>
            <div class="mt-4">
              <label class="label">
                <span class="label-text font-medium">Commentaire / Motif</span>
              </label>
              <textarea
                v-model="reason"
                class="textarea textarea-bordered w-full"
                rows="3"
                placeholder="Motif de la régularisation..."
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Barre sticky -->
    <div v-if="selectedQuote" class="sticky bottom-0 border-t border-base-200 bg-base-100/95 backdrop-blur">
      <div class="max-w-[1600px] mx-auto w-full p-4 flex items-center justify-end gap-4">
        <ButtonDaisy text="Annuler" variant="ghost" @click="goBack" />
        <ButtonDaisy
          text="Créer"
          variant="secondary"
          :loader="isSaving"
          :disabled="!canSave || !canCreateRegulation"
          @click="showModalCreate = true"
        />
        <ButtonDaisy
          v-if="canAdvWrite"
          text="Créer et Valider"
          variant="primary"
          :loader="isSaving"
          :disabled="!canSave"
          @click="showModalCreateAndValidate = true"
        />
      </div>
    </div>

    <ConfirmModal
      :show-modal="showModalCreate"
      title="Créer la régularisation"
      confirm-text="Créer"
      cancel-text="Annuler"
      confirm-variant="secondary"
      :loading="isSaving"
      @confirm="onSave(false)"
      @cancel="showModalCreate = false"
    >
      <template #body>
        <p class="text-base-content/80">Confirmer la création de la régularisation pour le devis {{ selectedQuote?.quoteReference }} ?</p>
      </template>
    </ConfirmModal>
    <ConfirmModal
      :show-modal="showModalCreateAndValidate"
      title="Créer et valider la régularisation"
      confirm-text="Créer et valider"
      cancel-text="Annuler"
      confirm-variant="primary"
      :loading="isSaving"
      @confirm="onSave(true)"
      @cancel="showModalCreateAndValidate = false"
    >
      <template #body>
        <p class="text-base-content/80">La régularisation sera créée et validée immédiatement. Confirmer ?</p>
      </template>
    </ConfirmModal>
  </div>
</template>
